import React, { useState, useEffect } from 'react';
import { Button } from 'antd';
import { FormattedMessage } from 'react-intl';
import { ArrowForward } from '@material-ui/icons';
import CustomerSupportTable from 'components/Dashboard/WorldCup/CustomerSupport/CustomerSupportTable';
import CustomerSupportFilter from 'components/Dashboard/WorldCup/CustomerSupport/CustomerSupportFilter';
import CustomerSupportModal from 'components/Dashboard/WorldCup/CustomerSupport/CustomerSupportModal';
import Loading from 'components/Basic/Loading';
import { useAppDispatch, useAppSelector } from 'utilities/redux';
import { useLazyGetParticipationsListQuery, useLazyGetActiveCampaignsQuery } from 'core/worldCup/customerSupport/CustomerSupportService';
import { setCustomerSupportData, setCampaigns, setSelectedCampaignId } from 'core/worldCup/customerSupport/CustomerSupportSlice';

const CustomerSupport: React.FC = () => {
  const [state, setState] = useState({
    isLoading: false,
    isShowModal: false,
    searchText: '',
    sortId: 'desc',
    sortDate: '',
  });
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const dispatch = useAppDispatch();
  const [getParticipationsList] = useLazyGetParticipationsListQuery();
  const [getActiveCampaigns] = useLazyGetActiveCampaignsQuery();

  const { list, selectedCampaignId, campaigns } = useAppSelector(state => state.customerSupportReducer) || {
    list: { data: [], meta: { totalCount: 0 } },
    selectedCampaignId: null,
    campaigns: [],
  };

  useEffect(() => {
    loadCampaigns();
    setState({ ...state, searchText: '' });
  }, []);

  useEffect(() => {
    setCurrent(1);
  }, [pageSize]);

  useEffect(() => {
    if (selectedCampaignId) {
      getInitialData();
    }
  }, [selectedCampaignId]);

  const loadCampaigns = async () => {
    try {
      const res = await getActiveCampaigns().unwrap();
      dispatch(setCampaigns(res));
      if (res.length > 0) {
        dispatch(setSelectedCampaignId(res[0].id));
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  };

  const handleCampaignChange = (campaignId: number) => {
    dispatch(setSelectedCampaignId(campaignId));
    setCurrent(1);
  };

  const getInitialData = async () => {
    if (!selectedCampaignId) return;
    setState(prev => ({ ...prev, isLoading: true }));

    const queryString = buildQueryString({
      search: '',
      page: 1,
      size: pageSize,
      sort_by_id: state.sortId,
    });

    try {
      const res = await getParticipationsList({ campaignId: selectedCampaignId, queryString }).unwrap();
      dispatch(setCustomerSupportData({ list: res }));
    } catch (error) {
      console.error('Error fetching participations list:', error);
    } finally {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleChangePage = async (page: number, size: number) => {
    if (!selectedCampaignId) return;
    setState(prev => ({ ...prev, isLoading: true }));
    setPageSize(size);

    const queryString = buildQueryString({
      search: state.searchText.trim(),
      page: page,
      size: size,
      sort_by_id: state.sortId,
      sort_by_date: state.sortDate,
    });

    try {
      const res = await getParticipationsList({ campaignId: selectedCampaignId, queryString }).unwrap();
      dispatch(setCustomerSupportData({ list: res }));
    } catch (e) {
      console.error(e);
    } finally {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleSearch = async (e: any) => {
    const searchTextValue = e.target.value.trimStart();
    setState(prev => ({ ...prev, searchText: searchTextValue }));
    if (e.keyCode === 13 && selectedCampaignId) {
      setCurrent(1);

      const queryString = buildQueryString({
        search: e.target.value.trim(),
        page: 1,
        size: pageSize,
        sort_by_id: state.sortId,
        sort_by_date: state.sortDate,
      });

      try {
        const res = await getParticipationsList({ campaignId: selectedCampaignId, queryString }).unwrap();
        dispatch(setCustomerSupportData({ list: res }));
      } catch (e) {
        console.error(e);
      }
    }
  };

  const handleSort = (value: string) => {
    if (value[0] === 'I') {
      setState(prev => ({ ...prev, sortId: value.substring(1), sortDate: '' }));
    } else if (value[0] === 'D') {
      setState(prev => ({ ...prev, sortDate: value.substring(1), sortId: '' }));
    }
  };

  const onApplyFilter = async () => {
    if (!selectedCampaignId) return;
    setState(prev => ({ ...prev, isLoading: true }));
    setCurrent(1);

    const queryString = buildQueryString({
      search: state.searchText.trim(),
      page: 1,
      size: pageSize,
      sort_by_id: state.sortId,
      sort_by_date: state.sortDate,
    });

    try {
      const res = await getParticipationsList({ campaignId: selectedCampaignId, queryString }).unwrap();
      dispatch(setCustomerSupportData({ list: res }));
    } catch (e) {
      console.error(e);
    } finally {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleViewDetail = (id: number) => {
    console.log('Ver detalle de participación:', id);
  };

  const handleCloseModal = () => {
    setState(prev => ({ ...prev, isShowModal: false }));
  };

  return (
    <div className="customerSupport_wrapper" id="customerSupport_wrapper" style={{ position: 'relative' }}>
      <Loading visible={state.isLoading} />
      <div className="btn_wrapper" id="new_participation_btn_wrapper">
        <Button
          className="add_btn"
          onClick={() => setState(prev => ({ ...prev, isShowModal: true }))}
          id="new_participation_btn"
        >
          <FormattedMessage id="Add participation" />
          <ArrowForward />
        </Button>
      </div>
      <CustomerSupportFilter
        searchText={state.searchText}
        handleSearch={handleSearch}
        onSort={handleSort}
        onApplyFilter={onApplyFilter}
        campaigns={campaigns}
        selectedCampaignId={selectedCampaignId}
        onCampaignChange={handleCampaignChange}
      />
      <CustomerSupportTable
        participationsData={list.data || null}
        onChangePage={(current, size) => handleChangePage(current, size)}
        total={list.meta?.totalCount || 0}
        setCurrent={setCurrent}
        current={current}
        pageSize={pageSize}
        setPageSize={setPageSize}
        onViewDetail={handleViewDetail}
      />
      <CustomerSupportModal
        mode="modal"
        isOpenModal={state.isShowModal}
        onClose={handleCloseModal}
        onSaveSuccess={() => handleChangePage(current, pageSize)}
      />
    </div>
  );
};

export default CustomerSupport;

const buildQueryString = ({ search = '', page = 1, size = 10, sort_by_id = 'desc', sort_by_date = '' }) => {
  const params = [`search=${encodeURIComponent(search)}`, `page=${page}`, `limit=${size}`];

  if (sort_by_id) {
    params.push(`sort=id`);
    params.push(`sortDirection=${sort_by_id}`);
  }

  if (sort_by_date) {
    params.push(`sort=createdAt`);
    params.push(`sortDirection=${sort_by_date}`);
  }

  return params.join('&');
};
